import { FormCard } from 'components/forms/FormComponents';
import useNeveForm from 'components/forms/NeveForm';
import TextField from 'components/forms/TextField';
import React from 'react';
import { requiredFieldErrorMessage, createErrorMessage, fieldErrorCodes } from 'shared/error-messages';
import { EmailPattern, HomepageContext } from './Homepage';
import { signIn, signOut } from 'aws-amplify/auth';
import { useNavigate } from 'react-router-dom';
import Notification, { ButtonToHelp, NotificationType } from 'components/notifiers/Notification';
import { AuthContext } from 'contextProviders/AuthContext';
import strings from 'strings/strings.json';
import { Headline2, TertiaryButton } from 'components/Elements';
import Button from 'components/buttons/Button';
import { TurnstileWidget } from './TurnstileWidget';
import { linkNeveUser } from 'lib/link-neve-user';

type SignInFormInput = {
    email: string;
    password: string;
};

type ErrorButton = {
    error: string;
    buttonToHelp?: ButtonToHelp;
};

export function SignInForm(): JSX.Element {
    const navigate = useNavigate();
    const context = React.useContext(AuthContext);
    const { setState, setSharedData } = React.useContext(HomepageContext);

    const [errorAndButton, setErrorAndButton] = React.useState<ErrorButton>();
    const [submittingSignIn, setSubmittingSignIn] = React.useState(false);
    const [turnstileToken, setTurnstileToken] = React.useState('');
    const [turnstileError, setTurnstileError] = React.useState('');
    const [turnstileResetSignal, setTurnstileResetSignal] = React.useState(0);
    const resetTurnstile = React.useCallback(() => setTurnstileResetSignal((prev) => prev + 1), []);
    const handleTurnstileTokenChange = React.useCallback((token: string) => setTurnstileToken(token), []);

    const {
        register,
        handleSubmit,
        formState: { errors },
        watch,
    } = useNeveForm<SignInFormInput>();

    const watchedEmail = watch('email');

    async function submitSignIn(data: SignInFormInput) {
        if (!turnstileToken) return;

        setSubmittingSignIn(true);
        try {
            await signOut(); //prevents UserAlreadyAuthenticatedException 'There is already a signed in user.'
            const result = await signIn({
                username: data.email,
                password: data.password,
                options: {
                    clientMetadata: {
                        turnstileToken,
                        turnstileAction: 'signin',
                    },
                },
            });

            if (result.nextStep.signInStep === 'CONFIRM_SIGN_UP') {
                setSharedData({
                    emailAddress: data.email,
                    password: data.password,
                });
                setState('confirmSignUp');
                return;
            }

            await linkNeveUser(data.email);
            await context.refreshUserData();
            navigate('/');
        } catch (error: any) {
            resetTurnstile();
            const errorName = error?.name ?? error?.__type ?? 'UnknownError';
            const errorMessage = error?.message ?? error?.toString();

            switch (errorName) {
                case 'UserNotFoundException':
                    setErrorAndButton({
                        error: strings.homepage.errors.signIn.userNotFoundException,
                        buttonToHelp: {
                            text: strings.homepage.buttons.signUp,
                            aria: strings.homepage.buttons.signUp,
                            action: () => setState('signUp'),
                        },
                    });
                    break;
                case 'NotAuthorizedException':
                    setErrorAndButton({
                        error: strings.homepage.errors.signIn.notAuthorizedException,
                        buttonToHelp: undefined,
                    });
                    break;
                default:
                    console.error('Unhandled sign-in error:', {
                        errName: errorName,
                        errMessage: errorMessage,
                        raw: error,
                    });
                    setErrorAndButton({
                        // error: strings.homepage.errors.signIn.general,
                        error: `${errorName}: ${errorMessage}`,
                        buttonToHelp: undefined,
                    });
                    break;
            }
        } finally {
            setSubmittingSignIn(false);
        }
    }

    function forgotPassword() {
        setSharedData((prev) => ({ ...prev, emailAddress: watchedEmail, password: '' }));
        setState('resetPassword');
    }

    return (
        <FormCard onSubmit={handleSubmit(submitSignIn)}>
            <Headline2>{strings.homepage.headlines.signIn}</Headline2>
            <Notification
                title={strings.homepage.signUpNotification}
                notificationType={NotificationType.INFO}
                maxWidth={'fit-content'}
                buttonOnSameRow={true}
                buttonToHelp={{
                    text: strings.homepage.buttons.signUp,
                    aria: strings.homepage.buttons.signUp,
                    action: () => setState('signUp'),
                }}
            />
            <TextField
                fieldName={strings.homepage.fields.email}
                labelText={strings.homepage.fields.email}
                inputAria={strings.homepage.fields.email}
                required
                errorMessage={errors.email?.message}
                {...register('email', {
                    required: {
                        value: true,
                        message: requiredFieldErrorMessage(strings.homepage.fields.email),
                    },
                    maxLength: {
                        value: 100,
                        message: createErrorMessage(strings.homepage.fields.email, fieldErrorCodes.maxLength),
                    },
                    pattern: {
                        value: EmailPattern,
                        message: strings.homepage.fields.emailPattern,
                    },
                })}
            />
            <TextField
                fieldName={strings.homepage.fields.password}
                labelText={strings.homepage.fields.password}
                inputAria={strings.homepage.fields.password}
                type="password"
                required
                errorMessage={errors.password?.message}
                {...register('password', {
                    required: {
                        value: true,
                        message: requiredFieldErrorMessage(strings.homepage.fields.password),
                    },
                })}
            />
            <TurnstileWidget
                action="signin"
                onTokenChange={handleTurnstileTokenChange}
                onError={setTurnstileError}
                resetSignal={turnstileResetSignal}
            />
            {turnstileError && (
                <Notification
                    title={turnstileError}
                    notificationType={NotificationType.DANGER}
                    maxWidth={'100%'}
                />
            )}
            <Button
                title={strings.homepage.buttons.signIn}
                aria={strings.homepage.buttons.signIn}
                type="submit"
                fullwidth
                submitting={submittingSignIn}
                disabled={!turnstileToken}
            />
            {errorAndButton && (
                <Notification
                    title={errorAndButton.error}
                    buttonToHelp={errorAndButton.buttonToHelp}
                    notificationType={NotificationType.DANGER}
                    maxWidth={'100%'}
                    buttonOnSameRow={true}
                />
            )}
            <div>
                <TertiaryButton aria-label={strings.homepage.buttons.forgotPassword} onClick={forgotPassword}>
                    {strings.homepage.buttons.forgotPassword}
                </TertiaryButton>
            </div>
        </FormCard>
    );
}
